<script setup>
import { reactive, ref, watch } from 'vue';
import { useEPStore } from 'stores/ep';
import moment from 'moment';

const EPStore = useEPStore();

const MODE_STORAGE_KEY = 'finderMode';

function savedMode() {
	try {
		return localStorage.getItem(MODE_STORAGE_KEY) === 'staircase' ? 'staircase' : 'legacy';
	} catch (e) {
		return 'legacy';
	}
}

const data = reactive({
	date: moment().format('YYYY-MM-DD'),
	startTime: '00:00',
	endTime: '23:59',
	// 'legacy' - the old signal score, 'staircase' - the detector behind the notifications
	mode: savedMode()
});

watch(() => data.mode, mode => {
	try {
		localStorage.setItem(MODE_STORAGE_KEY, mode);
	} catch (e) {
		// The choice is just not remembered.
	}
});

// The mode the shown results were found with.
const resultMode = ref(data.mode);

const sorting = ref(false);
const clickedItems = reactive({});
const foundedData = ref(null);
const timer = ref(null);

let timerInterval;
let seconds = 0;
let minutes = 0;
let hours = 0;

function fixZero(number) {
	return number < 10 ? `0${number}` : number;
}

function startTimer() {
	seconds = 0;
	minutes = 0;
	hours = 0;

	timer.value = '00:00:00';

	clearInterval(timerInterval);
	timerInterval = setInterval(updateTimer, 1000);
}

function stopTimer() {
	clearInterval(timerInterval);
}

function updateTimer() {
	seconds++;

	if (seconds === 60) {
		seconds = 0;
		minutes++;

		if (minutes === 60) {
			minutes = 0;
			hours++;
		}
	}

	timer.value = `${fixZero(hours)}:${fixZero(minutes)}:${fixZero(seconds)}`;
}

function getRoundedHour() {
	const now = moment();

	let rounded = now
		.clone()
		.startOf('hour')
		.add(now.minutes() >= 30 ? 1 : 0, 'hour');

	const dayStart = now.clone().startOf('day');
	const lastFullHour = now.clone().startOf('day').hour(23);

	// Neleidžiam apvalinimui persikelti į kitą dieną
	if (rounded.isAfter(lastFullHour)) {
		rounded = lastFullHour;
	}

	if (rounded.isBefore(dayStart)) {
		rounded = dayStart;
	}

	return rounded;
}

function setUpcomingRange() {
	const roundedNow = getRoundedHour();
	const dayStart = roundedNow.clone().startOf('day');
	const dayEnd = roundedNow.clone().endOf('day');

	let start = roundedNow.clone().subtract(1, 'hour');
	let end = roundedNow.clone().add(3, 'hours');

	// Minimali pradžia 00:00
	if (start.isBefore(dayStart)) {
		start = dayStart;
	}

	// Maksimali pabaiga 23:59
	if (end.isAfter(dayEnd)) {
		end = dayEnd;
	}

	data.date = roundedNow.format('YYYY-MM-DD');
	data.startTime = start.format('HH:mm');
	data.endTime = end.format('HH:mm');
}

function setUntilEndOfDayRange() {
	const roundedNow = getRoundedHour();
	const dayStart = roundedNow.clone().startOf('day');

	let start = roundedNow.clone().subtract(1, 'hour');

	// Minimali pradžia 00:00
	if (start.isBefore(dayStart)) {
		start = dayStart;
	}

	data.date = roundedNow.format('YYYY-MM-DD');
	data.startTime = start.format('HH:mm');
	data.endTime = '23:59';
}

function sortByTime() {
	foundedData.value.sort((a, b) => {
		const startTimeA = new Date(a.time);
		const startTimeB = new Date(b.time);

		if (sorting.value) {
			return resultMode.value === 'staircase' ? b.steps - a.steps : b.difference - a.difference;
		}

		return startTimeA - startTimeB;
	});

	sorting.value = !sorting.value;
}

function find() {
	document.title = 'Vyksta rungtynių skenavimas';

	foundedData.value = null;

	startTimer();

	Object.keys(clickedItems).forEach(key => {
		delete clickedItems[key];
	});

	resultMode.value = data.mode;

	EPStore.fetchEvents(
		data.date,
		data.startTime,
		data.endTime,
		data.mode
	).then(response => {
		if (response?.data?.success) {
			foundedData.value = response.data.data;
		}

		stopTimer();
		document.title = 'Skenavimas baigtas';
	});
}

function redirectToAnalyzer(url, index, type) {
	clickedItems[index] = true;

	const preparedUrl = `${url}&type=${type}`;

	window.open(preparedUrl, '_blank');
}
</script>

<template>
	<div class="d-flex flex-column align-items-center gap-4 w-100">

		<div class="col-12 col-lg-9 row g-3">

			<!-- Data -->
			<div class="col-12 col-sm-6 col-md-3 col-xl-2">
				<label for="date">Data</label>

				<input
					v-model="data.date"
					id="date"
					type="date"
					class="form-control mt-1 fs-7"
				/>
			</div>

			<!-- Nuo -->
			<div class="col-6 col-sm-3 col-md-2 col-xl-2">
				<label for="startTime">Nuo</label>

				<input
					v-model="data.startTime"
					id="startTime"
					type="time"
					class="form-control mt-1 fs-7"
				/>
			</div>

			<!-- Iki -->
			<div class="col-6 col-sm-3 col-md-2 col-xl-2">
				<label for="endTime">Iki</label>

				<input
					v-model="data.endTime"
					id="endTime"
					type="time"
					class="form-control mt-1 fs-7"
				/>
			</div>

			<!-- Greiti intervalai -->
			<div class="col-12 col-xl-6">
				<label>Greitas intervalas</label>

				<div class="btn-group w-100 mt-1">
					<button
						type="button"
						class="btn btn-outline-dark fs-7"
						@click="setUpcomingRange"
					>
						-1 val. → +3 val.
					</button>

					<button
						type="button"
						class="btn btn-outline-dark fs-7"
						@click="setUntilEndOfDayRange"
					>
						-1 val. → 23:59
					</button>
				</div>
			</div>

			<!-- Algoritmas -->
			<div class="col-12 col-xl-6">
				<label>Algoritmas</label>

				<div class="btn-group w-100 mt-1">
					<button
						type="button"
						:class="['btn fs-7', data.mode === 'legacy' ? 'btn-dark' : 'btn-outline-dark']"
						@click="data.mode = 'legacy'"
					>
						Senas algoritmas
					</button>

					<button
						type="button"
						:class="['btn fs-7', data.mode === 'staircase' ? 'btn-dark' : 'btn-outline-dark']"
						@click="data.mode = 'staircase'"
					>
						Laiptų detektorius
					</button>
				</div>
			</div>

			<!-- Ieškoti -->
			<div class="col-12 offset-xl-4 col-xl-2 d-flex flex-column justify-content-end">
				<button
					type="button"
					@click="find"
					class="btn btn-dark w-100 fs-7"
				>
					Ieškoti
				</button>
			</div>

		</div>

		<div class="col-12 col-lg-9 d-flex flex-column gap-4 px-2">

			<div
				class="d-flex flex-column align-items-center flex-md-row justify-content-md-between gap-2"
			>
				<span v-if="timer">
					Užklausos trukmė:
					<span class="fw-bold">
						{{ timer }}
					</span>
				</span>

				<span
					v-if="foundedData"
					class="text-center"
				>
					Atitiko algoritmą:
					<span class="fw-bold">
						{{ foundedData.length }}
					</span>
				</span>
			</div>

			<div
				v-if="foundedData && foundedData.length"
				class="table-responsive"
			>

				<div
					class="col-12 offset-xl-8 col-xl-4 d-flex flex-column align-items-end mb-3"
				>
					<button
						type="button"
						@click="sortByTime"
						class="btn btn-dark w-100 fs-7"
						:style="sorting ? 'color: #c7f683' : ''"
					>
						Rūšiuoti pagal laiką
					</button>
				</div>

				<table
					class="table finder-table"
					style="vertical-align: middle"
				>
					<thead>
					<tr>
						<th
							scope="col"
							style="width: 25px"
						></th>

						<th scope="col">
							Laikas
						</th>

						<th scope="col">
							Pavadinimas
						</th>

						<template v-if="resultMode === 'staircase'">
							<th scope="col">
								Laipt.
							</th>

							<th scope="col">
								Koef.
							</th>

							<th scope="col">
								Prognozė
							</th>
						</template>

						<th
							v-else
							scope="col"
						>
							D
						</th>

						<th scope="col">
							T
						</th>
					</tr>
					</thead>

					<tbody>
					<tr
						v-for="(item, index) in foundedData"
						:key="index"
						@click="redirectToAnalyzer(item.url, index, item.side)"
						class="cursor-pointer"
					>
						<td>
							<i
								:class="[
										clickedItems[index]
											? 'fa-solid'
											: 'fa-regular',
										'fa-circle-check text-primary'
									]"
							></i>
						</td>

						<td>
							{{ item.time }}
						</td>

						<td>
							{{ item.home }} - {{ item.away }}
						</td>

						<template v-if="resultMode === 'staircase'">
							<td>
								{{ item.steps }}
							</td>

							<td class="text-nowrap">
								{{ item.openingOdd.toFixed(2) }} → {{ item.currentOdd.toFixed(2) }}
							</td>

							<td class="text-nowrap">
								{{ item.team }}
								{{ item.handicap.line > 0 ? '+' : '' }}{{ item.handicap.line }}
								<span class="text-secondary">(≈{{ item.handicap.odd.toFixed(2) }})</span>
							</td>
						</template>

						<td v-else>
							{{ item.difference }}
						</td>

						<td>
							{{ item.side === 'positive' ? 'P' : 'N' }}
						</td>
					</tr>
					</tbody>
				</table>

			</div>

		</div>

	</div>
</template>
